import React, { useState, useContext } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    IconButton,
    Divider,
    Stack,
    Alert,
    CircularProgress,
    useTheme,
    useMediaQuery
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RestoreIcon from "@mui/icons-material/Restore";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { StateContext } from "../../../../components/context/StateProvider";
import Toasts from "../../../../components/notificacion/Toasts";
import { BADGE_COLORS } from "../../../../components/ui/badgeColors";

const MENSAJE_ERROR_GENERICO = "No se pudo restaurar al usuario. Inténtalo de nuevo.";

/**
 * Modal de confirmación para restaurar a un usuario dado de baja.
 *
 * Reactiva el acceso (fecha_baja -> NULL) sin tocar el historial, que nunca se
 * perdió. Invoca `restaurarUsuario` de useUsuarios directamente, gestionando su
 * propio loading local y su propio toast (éxito, o el 400 del backend "no estaba
 * dado de baja" tal cual en error); el refresco de la lista lo hace el propio hook,
 * este modal solo se cierra al terminar con éxito.
 */
export default function ModalRestaurarUsuario({ show, onHide, usuario, restaurarUsuario }) {
    const { state, updateState } = useContext(StateContext);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [loading, setLoading] = useState(false);

    const mostrarToast = (mensaje, tipo) => {
        const toast = Toasts.generateToast(mensaje, tipo);
        updateState({ toasts: [...state.toasts, toast] });
    };

    const handleConfirmar = async () => {
        if (!usuario) return;

        setLoading(true);
        try {
            await restaurarUsuario(usuario.id);
            mostrarToast(`Usuario "${usuario.username}" restaurado correctamente`, "success");
            onHide();
        } catch (error) {
            mostrarToast(error.response?.data?.message || MENSAJE_ERROR_GENERICO, "error");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            open={show}
            onClose={loading ? undefined : onHide}
            maxWidth="xs"
            fullWidth
            fullScreen={isMobile}
            PaperProps={{
                sx: {
                    borderRadius: isMobile ? 0 : 2,
                    boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                    m: isMobile ? 0 : 2
                }
            }}
        >
            {/* Header */}
            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: { xs: 1, sm: 2 },
                    pb: { xs: 1.5, sm: 2 },
                    pt: { xs: 2, sm: 2.5 },
                    px: { xs: 2, sm: 3 }
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, sm: 1.5 } }}>
                    <Box
                        sx={{
                            width: { xs: 36, sm: 40 },
                            height: { xs: 36, sm: 40 },
                            borderRadius: "50%",
                            backgroundColor: BADGE_COLORS.GREEN.bg,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0
                        }}
                    >
                        <RestoreIcon sx={{ color: BADGE_COLORS.GREEN.text, fontSize: { xs: 20, sm: 24 } }} />
                    </Box>
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 700,
                            color: "#212529",
                            fontSize: { xs: "1.1rem", sm: "1.25rem" }
                        }}
                    >
                        {isMobile ? "Restaurar" : "Confirmar restauración"}
                    </Typography>
                </Box>
                <IconButton
                    onClick={onHide}
                    disabled={loading}
                    size="small"
                    sx={{
                        color: "#6c757d",
                        "&:hover": { backgroundColor: "rgba(0,0,0,0.04)" }
                    }}
                >
                    <CloseIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                </IconButton>
            </DialogTitle>

            <Divider />

            {/* Content */}
            <DialogContent
                sx={{
                    pt: { xs: 2, sm: 3 },
                    pb: { xs: 2, sm: 2 },
                    px: { xs: 2, sm: 3 },
                    flex: isMobile ? 1 : "initial"
                }}
            >
                <Stack spacing={{ xs: 2, sm: 2.5 }}>
                    <Typography sx={{ fontSize: { xs: 14, sm: 15 }, color: "#495057", lineHeight: 1.6 }}>
                        ¿Deseas restaurar a{" "}
                        <strong style={{ color: BADGE_COLORS.GREEN.text }}>{usuario?.username}</strong>? Podrá volver
                        a iniciar sesión de inmediato y conserva todo su historial, que nunca se perdió.
                    </Typography>

                    {usuario?.email && (
                        <Box
                            sx={{
                                p: { xs: 1.5, sm: 2 },
                                backgroundColor: "#f8f9fa",
                                borderRadius: 2,
                                border: "1px solid #e9ecef"
                            }}
                        >
                            <Typography sx={{ fontSize: { xs: 11, sm: 12 }, fontWeight: 600, color: "#6c757d", mb: 0.5 }}>
                                Email
                            </Typography>
                            <Typography sx={{ fontSize: { xs: 13, sm: 14 }, color: "#495057" }}>
                                {usuario.email}
                            </Typography>
                        </Box>
                    )}

                    <Alert
                        severity="success"
                        icon={<CheckCircleOutlineIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />}
                        sx={{
                            backgroundColor: BADGE_COLORS.GREEN.bg,
                            color: BADGE_COLORS.GREEN.text,
                            border: `1px solid ${BADGE_COLORS.GREEN.border}40`,
                            "& .MuiAlert-icon": { color: BADGE_COLORS.GREEN.text }
                        }}
                    >
                        <Typography sx={{ fontSize: { xs: 12, sm: 13 } }}>
                            Recuperará el acceso con su username y contraseña actuales, sin necesidad de crear una
                            cuenta nueva.
                        </Typography>
                    </Alert>
                </Stack>
            </DialogContent>

            <Divider />

            {/* Actions */}
            <DialogActions
                sx={{
                    p: { xs: 2, sm: 2.5 },
                    gap: { xs: 1, sm: 1.5 },
                    flexDirection: { xs: "column-reverse", sm: "row" }
                }}
            >
                <Button
                    variant="outlined"
                    onClick={onHide}
                    fullWidth={isMobile}
                    disabled={loading}
                    sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        px: { xs: 2, sm: 2.5 },
                        py: { xs: 1.25, sm: 1 },
                        fontSize: { xs: 14, sm: 15 },
                        color: "#6c757d",
                        borderColor: "#6c757d",
                        "&:hover": { borderColor: "#495057", backgroundColor: "rgba(0,0,0,0.04)" }
                    }}
                >
                    Cancelar
                </Button>
                <Button
                    variant="contained"
                    onClick={handleConfirmar}
                    fullWidth={isMobile}
                    disabled={loading}
                    startIcon={
                        loading ? (
                            <CircularProgress size={16} sx={{ color: "#fff" }} />
                        ) : (
                            <RestoreIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                        )
                    }
                    sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        px: { xs: 2, sm: 2.5 },
                        py: { xs: 1.25, sm: 1 },
                        fontSize: { xs: 14, sm: 15 },
                        backgroundColor: BADGE_COLORS.GREEN.border,
                        "&:hover": { backgroundColor: "#219150" },
                        "&:disabled": { backgroundColor: "#6c757d", color: "#fff" }
                    }}
                >
                    {loading ? "Restaurando..." : "Restaurar usuario"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
